// src/app/login/session.service.ts
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { first } from 'rxjs/operators';
import { LoginService } from './login.service';

// Schleifen-Deckel für Sprünge zu Auth0 (WERKZEUG-STANDARD 1.3.4). Liegt im
// sessionStorage: gilt für den Reiter, in dem eine Schleife laufen würde.
// Nach REDIRECT_WINDOW_MS zählt ein Sprung wieder als erster, damit eine
// normale Neuanmeldung nach Stunden nicht am Deckel hängen bleibt.
const REDIRECT_COUNT_KEY = '__portal_auth_redirects__';
const MAX_REDIRECTS = 2;
const REDIRECT_WINDOW_MS = 10 * 60_000;

// Puffer wie beim Kachel-Klick und im Backend (Uhrenabweichung 60 s).
const EXPIRY_MARGIN_MS = 60_000;

/**
 * Gültiges ID-Token für Backend-Aufrufe und die EINZIGE Stelle, die nach
 * einem 401 abmeldet.
 */
@Injectable({
  providedIn: 'root'
})
export class SessionService {
  /** Einmal-Guard: mehrere gleichzeitige 401 ergeben genau einen Sprung. */
  private leaving = false;
  private renewal: Promise<string | null> | null = null;

  constructor(
    private loginService: LoginService,
    private authService: AuthService
  ) {}

  /**
   * Gespeichertes ID-Token, wenn es noch mindestens 60 s gilt. Sonst still bei
   * Auth0 erneuern (wie der Kachel-Klick). null, wenn das nicht gelingt.
   */
  validToken(): Promise<string | null> {
    if (this.storedTokenValid()) {
      return Promise.resolve(this.loginService.getJWTToken());
    }
    if (!this.renewal) {
      this.renewal = this.renew().finally(() => (this.renewal = null));
    }
    return this.renewal;
  }

  /**
   * Neue Anmeldung bei Auth0; mit bestehender Auth0-Sitzung ohne Eingabe.
   * @returns false, wenn nicht gesprungen wird (Deckel erreicht).
   */
  reauthenticate(): boolean {
    if (this.leaving || !this.takeRedirectSlot()) return false;
    this.leaving = true;
    this.authService.loginWithRedirect();
    return true;
  }

  /**
   * Reaktion auf 401 der eigenen Backend-Basis: lokal abmelden, dann einmal
   * Auth0-Logout. Das `returnTo` des Werkzeugs bleibt im sessionStorage
   * (return-to.ts) und greift nach der neuen Anmeldung.
   */
  handleUnauthorized(): void {
    if (this.leaving) return;
    this.leaving = true;

    // Sonst schickt der nächste Seitenstart das abgelehnte Token wieder mit.
    this.loginService.logout();

    // Deckel erreicht: kein weiterer Sprung, keine Schleife Portal <-> Auth0.
    if (!this.takeRedirectSlot()) return;

    this.authService.logout({ logoutParams: { returnTo: window.location.origin } });
  }

  private storedTokenValid(): boolean {
    const token = this.loginService.getJWTToken();
    const exp = this.loginService.getExpirationToken();
    return !!token && !!exp && Date.now() < exp - EXPIRY_MARGIN_MS;
  }

  private async renew(): Promise<string | null> {
    // 'on' zuerst: der Auth0-Speicher kann neuer sein als MU_JWT. Er läuft aber
    // nach dem Access-Token ab, nicht nach dem ID-Token; ist das ID-Token darin
    // schon abgelaufen, erzwingt 'off' die Erneuerung bei Auth0.
    for (const cacheMode of ['on', 'off'] as const) {
      try {
        await this.authService
          .getAccessTokenSilently({ cacheMode })
          .pipe(first())
          .toPromise();
        const claims = await this.authService.idTokenClaims$
          .pipe(first())
          .toPromise();
        if (claims && claims.__raw) {
          const expMs = (claims.exp ?? 0) * 1000;
          this.loginService.setExpirationToken(expMs);
          this.loginService.setJWTToken(claims.__raw, expMs);
        }
      } catch {
        return null;
      }
      if (this.storedTokenValid()) return this.loginService.getJWTToken();
    }
    return null;
  }

  private takeRedirectSlot(): boolean {
    try {
      const now = Date.now();
      const last = JSON.parse(sessionStorage.getItem(REDIRECT_COUNT_KEY) || 'null');
      const fresh = !last || now - last.since > REDIRECT_WINDOW_MS;
      const count = fresh ? 0 : Number(last.count) || 0;
      if (count >= MAX_REDIRECTS) return false;
      sessionStorage.setItem(
        REDIRECT_COUNT_KEY,
        JSON.stringify({ count: count + 1, since: fresh ? now : last.since })
      );
      return true;
    } catch {
      // Ohne Speicher kein Deckel - dann lieber nicht springen.
      return false;
    }
  }
}
